import type { TableColumnDefinition } from "$components/table";
import { alpha, Box, styled, Typography } from "@material-ui/core";
import {
  endOfMonth,
  format,
  isWithinInterval,
  parse,
  startOfMonth,
} from "date-fns";
import { type FC, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { RegisterTable } from "../../_lib/register-table";
import type { RegisterRow } from "./mapper";

const TableContainer = styled(Box)({
  height: "calc(100vh - 300px)",
  display: "flex",
  flexDirection: "column",
  "& .MuiTableContainer-root": {
    flex: 1,
    overflow: "auto",
    position: "relative",
  },
  "& .MuiTable-root": {
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  "& .MuiTableHead-root": {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: "white",
  },
  "& .MuiTableCell-head": {
    backgroundColor: "white",
    borderBottom: "2px solid rgba(224, 224, 224, 1)",
  },
  "& .MuiTableBody-root": {
    position: "relative",
  },
  "& .MuiTableRow-root[data-testid='empty-row']": {
    position: "relative",
    height: "100px",
  },
  "& .MuiTableRow-root[data-testid='empty-row'] .MuiTableCell-root": {
    padding: 0,
    border: "none",
  },
});

export const ChildImmunisationRegisterTable: FC<{
  currentPage: number;
  setCurrentPage: (page: number) => void;
  loading: boolean;
  rows: Array<RegisterRow>;
  selectedMonth: string;
  totalRows: number;
}> = ({
  currentPage,
  setCurrentPage,
  loading,
  rows,
  selectedMonth,
  totalRows,
}) => {
  const { formatMessage } = useIntl();

  const transformedRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        patientUuid: row.patientId,
        id: row.patientId,
      })),
    [rows],
  );

  const monthInterval = useMemo(() => {
    const monthStart = startOfMonth(
      parse(selectedMonth, "yyyy-MM", new Date()),
    );
    return {
      start: monthStart,
      end: endOfMonth(monthStart),
    };
  }, [selectedMonth]);

  const isDateInSelectedMonth = useCallback(
    (date: string) => {
      if (!date) return false;
      return isWithinInterval(new Date(date), monthInterval);
    },
    [monthInterval],
  );
  const renderVaccinationDate = useCallback(
    (value: string) => {
      if (!value) return "";
      const formattedDate = format(new Date(value), "dd MMM yyyy");

      return isDateInSelectedMonth(value) ? (
        <HighlightedCell>{formattedDate}</HighlightedCell>
      ) : (
        <UnhighlightedCellText>{formattedDate}</UnhighlightedCellText>
      );
    },
    [isDateInSelectedMonth],
  );

  const baseColumns = useMemo(
    () => [
      {
        field: "dateOfBirth",
        headerName: formatMessage({
          id: "childImmunisationRegister.header.DOB",
          defaultMessage: "DOB",
        }),
        width: 120,
        alignContents: "left",
        isSticky: true,
      } as TableColumnDefinition,
      {
        field: "name",
        headerName: formatMessage({
          id: "childImmunisationRegister.header.name",
          defaultMessage: "Name",
        }),
        width: 250,
        alignContents: "left",
        isSticky: true,
        renderCell: (value: string) => <StyledName>{value}</StyledName>,
      } as TableColumnDefinition,
      {
        field: "gender",
        headerName: formatMessage({
          id: "childImmunisationRegister.header.sex",
          defaultMessage: "Sex",
        }),
        width: 70,
        alignContents: "left",
      } as TableColumnDefinition,
      {
        field: "address",
        headerName: formatMessage({
          id: "childImmunisationRegister.header.address",
          defaultMessage: "Address",
        }),
        width: 280,
        alignContents: "left",
      } as TableColumnDefinition,
      {
        field: "phone",
        headerName: formatMessage({
          id: "childImmunisationRegister.header.phone",
          defaultMessage: "Guardian Phone",
        }),
        width: 120,
        alignContents: "left",
      } as TableColumnDefinition,
    ],
    [formatMessage],
  );

  const vaccineColumns = useMemo(
    () =>
      vaccineNameColumns.map(
        (name) =>
          ({
            field: name,
            headerName: name,
            width: 120,
            alignContents: "left",
            renderCell: renderVaccinationDate,
          }) as TableColumnDefinition,
      ),
    [renderVaccinationDate],
  );

  const columns = useMemo(
    () => [...baseColumns, ...vaccineColumns],
    [baseColumns, vaccineColumns],
  );

  return (
    <TableContainer>
      <RegisterTable
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        loading={loading}
        rows={transformedRows}
        columns={columns}
        totalRows={totalRows}
        errorBoundaryName="child-immunisation-register-table"
        centerPagination={true}
      />
    </TableContainer>
  );
};

const vaccineNameColumns = [
  "HEP B 0",
  "BCG",
  "OPV 0",
  "PENTA 1",
  "PCV 1",
  "ROTA 1",
  "OPV 1",
  "IPV 1",
  "PENTA 2",
  "PCV 2",
  "ROTA 2",
  "OPV 2",
  "PENTA 3",
  "PCV 3",
  "ROTA 3",
  "OPV 3",
  "IPV 2",
  "VITAMIN A 1",
  "VITAMIN A 2",
  "MEASLES 1",
  "YELLOW FEVER",
  "MENINGITIS A",
  "MEASLES 2",
  "HPV",
] as const;

const HighlightedCell = styled("div")(({ theme }) => ({
  margin: theme.spacing(-3),
  padding: "18px",
  backgroundColor: alpha(theme.palette.brand.main, 0.1),
  display: "flex",
  alignItems: "center",
  fontWeight: "bold",
}));

const UnhighlightedCellText = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[500],
  fontSize: "14px",
}));

const StyledName = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: "bold",
}));
